import { CONFIG } from "./config";
import { mulberry32 } from "./rng";
import type { CatalogEntry } from "./songs/catalog";
import { demoCandidates, pickDemo } from "./songs/song-pick";

/** Side-effecting seams the controller drives; `main.ts` provides them. */
export interface DemoHost {
  catalog(): CatalogEntry[];
  /** A non-demo (user/default) song is currently loaded. The host distinguishes
   *  demo songs because they run in engine mode "auto". */
  hasUserSong(): boolean;
  /** Load a catalog song, engine in auto mode, playing immediately (no persist). */
  startAuto(id: string): void;
  /** Leave the reel: restore the user's song (last played, else default), paused in wait. */
  loadUserSong(): void;
}

/** The no-MIDI attract reel + first-song state machine. Engine-unaware; owns only
 *  the active flag and a seeded RNG. See the spec's boot table. */
export class DemoController {
  private active = false;
  private readonly rng = mulberry32(CONFIG.firstRun.demoSeed);

  constructor(private readonly host: DemoHost) {}

  get isActive(): boolean {
    return this.active;
  }

  /** Called once at boot and on every MIDI hot-plug. */
  onMidiChange(hasMidi: boolean): void {
    if (hasMidi) {
      this.active = false;
      if (!this.host.hasUserSong()) this.host.loadUserSong();
      return;
    }
    // No working MIDI ⇒ the silent demo reel runs — any time the device is absent
    // (boot, reload, or a mid-session loss), even with a song loaded. The saved song
    // returns via loadUserSong the moment a device reconnects.
    if (!this.active) this.startNext();
  }

  /** The one-shot boot reconcile, once the MIDI state is finally known. `boot()` makes
   *  the catalog pickable (`initCatalog`) before that point, so a pick can land while
   *  `initMidi()` is still resolving — too early to stop a reel that has not started, and
   *  invisible to the reel when it does. That pick is live user intent: never open the reel
   *  over it, and leave the in-flight load to settle rather than restoring on top of it.
   *  Only the boot decision defers — `onMidiChange` still demos on any later device loss,
   *  song loaded or not. */
  reconcileAtBoot(hasMidi: boolean, userPicked: boolean): void {
    if (userPicked) return;
    this.onMidiChange(hasMidi);
  }

  /** A demo song finished — pick the next one (no-op unless the reel is running). */
  next(): void {
    if (this.active) this.startNext();
  }

  /** Leave the reel because the user picked/loaded a real song. */
  stop(): void {
    this.active = false;
  }

  private startNext(): void {
    const cands = demoCandidates(this.host.catalog());
    if (cands.length === 0) {
      this.active = false;
      return;
    }
    this.active = true;
    this.host.startAuto(pickDemo(cands, this.rng).id);
  }
}
